import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Filter, 
  Star, LayoutGrid, List
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AddVehicleModal from '@/components/admin/AddVehicleModal';
import EditVehicleModal from '@/components/admin/EditVehicleModal';
import DeleteConfirmDialog from '@/components/admin/DeleteConfirmDialog';
import VehicleStatusChangeModal from '@/components/admin/VehicleStatusChangeModal';
import { vehiclePrimaryImage } from '@/utils/vehicleImages';
import { vehicleMatchesSearch } from '@/utils/vehicleSearch';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, formatVehiclePrice } from '@/utils/vehiclePrice';
import AdminPageHeader from '@/components/admin/AdminPageHeader';
import AdminKpiGrid, { AdminKpiTile } from '@/components/admin/AdminKpiGrid';
import AdminFilterBar from '@/components/admin/AdminFilterBar';
import AdminRecordCard from '@/components/admin/AdminRecordCard';
import AdminOverflowMenu from '@/components/admin/AdminOverflowMenu';
import AdminVehicleCard from '@/components/admin/AdminVehicleCard';
import { vehicleStatusClass, vehicleStatusLabel } from '@/utils/vehicleStatus';

const VIEW_KEY = 'admin-inventory-view';
const FILTER_KEY = 'admin-inventory-filters';

function readSavedFilters() {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) || '{}');
  } catch {
    return {};
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const savedFilters = readSavedFilters();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(savedFilters.search || '');
  const [statusFilter, setStatusFilter] = useState(savedFilters.status || 'all');
  const [featuredOnly, setFeaturedOnly] = useState(Boolean(savedFilters.featuredOnly));
  const [pageSize, setPageSize] = useState(savedFilters.pageSize || DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(savedFilters.page || 1);
  const [view, setView] = useState(() => (localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'));
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [statusVehicle, setStatusVehicle] = useState(null);
  const [soldCount, setSoldCount] = useState(0);

  // Fetch vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(response.data);
      setFilteredVehicles(response.data);
      try {
        const soldRes = await axios.get(`${BACKEND_URL}/api/admin/vehicles`, {
          params: { status: 'sold' },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSoldCount(Array.isArray(soldRes.data) ? soldRes.data.length : 0);
      } catch {
        setSoldCount(0);
      }
    } catch (error) {
      toast.error('Failed to load vehicles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchVehicles();
    }
  }, [isAuthenticated, token, fetchVehicles]);

  // Filter vehicles
  useEffect(() => {
    let filtered = vehicles;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((v) => vehicleMatchesSearch(v, searchTerm));
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((v) => v.status === statusFilter);
    }

    if (featuredOnly) {
      filtered = filtered.filter((v) => v.featured);
    }

    setFilteredVehicles(filtered);
  }, [searchTerm, statusFilter, featuredOnly, vehicles]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_KEY,
        JSON.stringify({
          search: searchTerm,
          status: statusFilter,
          featuredOnly,
          page: currentPage,
          pageSize,
        }),
      );
    } catch {
      /* ignore */
    }
  }, [searchTerm, statusFilter, featuredOnly, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedVehicles = filteredVehicles.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentPage]);

  // no-op placeholder

  // Handle delete
  const handleDelete = async () => {
    if (!selectedVehicle) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/vehicles/${selectedVehicle.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Vehicle deleted successfully');
      fetchVehicles();
      setIsDeleteDialogOpen(false);
      setSelectedVehicle(null);
    } catch (error) {
      toast.error('Failed to delete vehicle');
      console.error(error);
    }
  };

  // Handle toggle featured
  const toggleFeatured = async (vehicle) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/vehicles/${vehicle.id}`,
        { featured: !vehicle.featured },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success(`Vehicle ${!vehicle.featured ? 'featured' : 'unfeatured'}`);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to update vehicle');
      console.error(error);
    }
  };

  const openStatusChange = (vehicle) => {
    setStatusVehicle(vehicle);
  };

  const getStatusBadge = (status) => (
    <span className={`px-3 py-1 text-xs font-['Manrope'] border ${vehicleStatusClass(status)}`}>
      {vehicleStatusLabel(status)}
    </span>
  );

  const setInventoryView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const cardActions = (vehicle) => [
    {
      label: 'Edit',
      onClick: () => {
        setSelectedVehicle(vehicle);
        setIsEditModalOpen(true);
      },
    },
    { label: 'Change status', onClick: () => openStatusChange(vehicle) },
    { label: vehicle.featured ? 'Unfeature' : 'Feature', onClick: () => toggleFeatured(vehicle) },
    {
      label: 'Delete',
      onClick: () => {
        setSelectedVehicle(vehicle);
        setIsDeleteDialogOpen(true);
      },
    },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
      <AdminPageHeader
        title="Inventory Dashboard"
        subtitle="Manage your vehicle inventory"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate('/admin/sold-vehicles')}
              className="min-h-11 px-5 py-2.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white font-['Manrope'] font-semibold"
              data-testid="sold-vehicles-btn"
            >
              Sold Vehicles
            </button>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="min-h-11 flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold"
            >
              <Plus className="w-5 h-5" />
              Add Vehicle
            </button>
          </>
        }
      />

      <div>
        <AdminKpiGrid>
          <AdminKpiTile
            label="Total Vehicles"
            value={vehicles.length}
            active={statusFilter === 'all' && !featuredOnly}
            testId="stat-inventory-total"
            onClick={() => {
              setStatusFilter('all');
              setFeaturedOnly(false);
              setCurrentPage(1);
            }}
          />
          <AdminKpiTile
            label="Available"
            value={vehicles.filter((v) => v.status === 'available').length}
            color="text-green-400"
            active={statusFilter === 'available' && !featuredOnly}
            testId="stat-inventory-available"
            onClick={() => {
              setCurrentPage(1);
              if (statusFilter === 'available' && !featuredOnly) {
                setStatusFilter('all');
                return;
              }
              setFeaturedOnly(false);
              setStatusFilter('available');
            }}
          />
          <AdminKpiTile
            label="Sold"
            value={soldCount}
            color="text-red-400"
            testId="stat-inventory-sold"
            onClick={() => navigate('/admin/sold-vehicles')}
          />
          <AdminKpiTile
            label="Featured"
            value={vehicles.filter((v) => v.featured).length}
            color="text-blue-400"
            active={featuredOnly}
            testId="stat-inventory-featured"
            onClick={() => {
              setCurrentPage(1);
              if (featuredOnly) {
                setFeaturedOnly(false);
                return;
              }
              setStatusFilter('all');
              setFeaturedOnly(true);
            }}
          />
        </AdminKpiGrid>

        <AdminFilterBar
          search={searchTerm}
          onSearch={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="Search by make, model, year, or VIN..."
        >
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full lg:w-auto bg-white/5 border border-white/10 text-white pl-11 pr-8 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="w-full lg:w-auto bg-white/5 border border-white/10 text-white px-4 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
            data-testid="admin-page-size-select"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} per page
              </option>
            ))}
          </select>
          <div className="flex border border-white/10">
            <button
              type="button"
              onClick={() => setInventoryView('grid')}
              className={`min-h-11 min-w-11 inline-flex items-center justify-center ${
                view === 'grid' ? 'bg-blue-500 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              aria-label="Grid view"
              aria-pressed={view === 'grid'}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setInventoryView('list')}
              className={`min-h-11 min-w-11 inline-flex items-center justify-center ${
                view === 'list' ? 'bg-blue-500 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              aria-label="List view"
              aria-pressed={view === 'list'}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </AdminFilterBar>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border border-white/10 bg-white/5 overflow-hidden">
                <div className="aspect-video bg-white/10 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-white/10 animate-pulse w-3/4" />
                  <div className="h-3 bg-white/10 animate-pulse w-1/2" />
                  <div className="h-3 bg-white/10 animate-pulse w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="bg-white/5 border border-white/10 p-12 text-center">
            {vehicles.length === 0 && !searchTerm && statusFilter === 'all' && !featuredOnly ? (
              <>
                <p className="text-white font-['Manrope'] mb-4">No vehicles yet.</p>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(true)}
                  data-testid="inventory-empty-add-vehicle"
                  className="min-h-11 inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Add Vehicle
                </button>
              </>
            ) : (
              <>
                <p className="text-white font-['Manrope'] mb-2">No vehicles match your filters</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setFeaturedOnly(false);
                    setCurrentPage(1);
                  }}
                  className="min-h-11 px-4 border border-white/15 text-white text-sm font-['Manrope'] hover:bg-white/5"
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {pagedVehicles.map((vehicle) => (
              <AdminVehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                overflowItems={cardActions(vehicle)}
              />
            ))}
          </div>
        ) : (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
            <div className="lg:hidden space-y-3 p-3">
              {pagedVehicles.map((vehicle) => (
                <AdminRecordCard
                  key={vehicle.id}
                  title={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  subtitle={vehicle.vin}
                  badge={getStatusBadge(vehicle.status)}
                  fields={[
                    { label: 'Price', value: formatVehiclePrice(vehicle) || 'Price TBD' },
                    { label: 'Mileage', value: `${Number(vehicle.mileage || 0).toLocaleString()} mi` },
                    { label: 'Featured', value: vehicle.featured ? 'Yes' : 'No' },
                  ]}
                  onClick={() => navigate(`/admin/vehicles/${vehicle.id}`)}
                  primary={
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/vehicles/${vehicle.id}`)}
                      className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                    >
                      View details
                    </button>
                  }
                  overflow={<AdminOverflowMenu items={cardActions(vehicle)} />}
                />
              ))}
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Mileage
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Featured
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pagedVehicles.map((vehicle) => (
                    <tr
                      key={vehicle.id}
                      className="hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/vehicles/${vehicle.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={vehiclePrimaryImage(vehicle)}
                            alt={`${vehicle.make} ${vehicle.model}`}
                            className="w-16 h-12 object-cover"
                          />
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="font-['Manrope'] font-semibold text-white">
                                {vehicle.make} {vehicle.model}
                              </div>
                              {Number(vehicle.linked_deal_count) > 0 && (
                                <span
                                  className="px-2 py-0.5 text-[10px] font-['Manrope'] font-semibold uppercase tracking-wide bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                                  data-testid={`linked-deal-tag-${vehicle.id}`}
                                >
                                  {Number(vehicle.linked_deal_count) === 1
                                    ? 'Linked to Deal'
                                    : `Linked to ${vehicle.linked_deal_count} Deals`}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-white/60 font-['Manrope']">
                              {vehicle.vin}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-['Manrope'] text-white">
                        {vehicle.year}
                      </td>
                      <td className="px-6 py-4 font-['Manrope'] text-white">
                        {formatVehiclePrice(vehicle) || (
                          <span className="text-amber-300">Price TBD</span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-['Manrope'] text-white/80">
                        {vehicle.mileage.toLocaleString()} mi
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusChange(vehicle);
                          }}
                          className="hover:opacity-80 transition-opacity"
                        >
                          {getStatusBadge(vehicle.status)}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFeatured(vehicle);
                          }}
                          className="transition-colors"
                        >
                          <Star
                            className={`w-5 h-5 ${
                              vehicle.featured
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-white/40'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVehicle(vehicle);
                              setIsEditModalOpen(true);
                            }}
                            className="p-2 hover:bg-white/10 transition-colors min-h-11 min-w-11"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVehicle(vehicle);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="p-2 hover:bg-white/10 transition-colors min-h-11 min-w-11"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </div>
        )}
          {filteredVehicles.length > 0 && !loading && (
            <div className="flex flex-wrap items-center justify-between gap-3 py-4">
              <p className="text-sm font-['Manrope'] text-white/50">
                Showing {pagedVehicles.length} of {filteredVehicles.length} · Page {safePage} of {totalPages}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[2.25rem] px-2 py-1.5 text-sm font-['Manrope'] ${
                        page === safePage
                          ? 'bg-blue-500 text-white'
                          : 'border border-white/10 text-white/70 hover:bg-white/5'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Modals */}
      <AddVehicleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          fetchVehicles();
          setIsAddModalOpen(false);
        }}
        token={token}
      />

      <EditVehicleModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedVehicle(null);
        }}
        onSuccess={() => {
          fetchVehicles();
          setIsEditModalOpen(false);
          setSelectedVehicle(null);
        }}
        vehicle={selectedVehicle}
        token={token}
      />

      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedVehicle(null);
        }}
        onConfirm={handleDelete}
        vehicleName={
          selectedVehicle
            ? `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}`
            : ''
        }
      />

      <VehicleStatusChangeModal
        isOpen={Boolean(statusVehicle)}
        vehicle={statusVehicle}
        token={token}
        mode="inventory"
        onClose={() => setStatusVehicle(null)}
        onSuccess={() => {
          fetchVehicles();
          setStatusVehicle(null);
        }}
      />
    </div>
  );
}
